import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminPaygEmailDialogComponent } from '../admin-payg-email-dialog/admin-payg-email-dialog.component';

export interface PaygCandidate {

  id: number;

  probateId: number;
  bequestId: number;
  beneficiaryId: number;

  beneficiaryName: string;
  beneficiaryURN: string;

  email?: string;

  aiEmail?: string;
  aiEmailCategory?: string;
  aiStatus?: string;

  bequestType?: string;

  estateValue?: number;

  cashAmount?: number;
  percentageAmount?: number;

  bequestValue?: number;

  verificationDate?: string;
  subscribed?: boolean;
  sendEmail: boolean;
  issueDate?: string;

  createdDate?: string;

  issueDateFrom?: Date;
  issueDateTo?: Date;
}


export interface PaygEmailAudit {
  id: number;

  bequestId: number;
  beneficiaryId: number;

  email?: string;

  subjectTemplate?: string;
  bodyTemplate?: string;

  subjectRendered?: string;
  bodyRendered?: string;

  sentDate?: string;

  status?: string;

  errorMessage?: string;

  sentBy?: string;

  azureOperationId?: string;
}


export interface PaygPaid {
  id: number;

  bequestId: number;
  beneficiaryId: number;

  probateId?: number;

  beneficiaryName?: string;
  beneficiaryURN?: string;

  email?: string;
  aiEmail?: string;

  amount: number;
  currency?: string;

  stripeCheckoutSessionId?: string;

  createdDate?: string;
  expiryDate?: string;
  paidDate?: string;

  status?: string;
}




@Component({
  selector: 'app-admin-payg',
  templateUrl: './admin-payg.component.html',
  styleUrl: './admin-payg.component.css',
  standalone: false
})
export class AdminPaygComponent {

  constructor(
    private http: HttpClient,
    private dialog: MatDialog

  ) { }

  @ViewChild(MatPaginator)
  paginator!: MatPaginator;

  paygCases: PaygCandidate[] = [];

  filteredPaygCases: PaygCandidate[] = [];

  pagedPaygCases: PaygCandidate[] = [];

  searchTerm = "";

  showWithEmail = true;
  showWithoutEmail = true;

  showWithAIEmail = true;
  showWithoutAIEmail = true;

  showFoundAIEmail = false;

  bequestTypes: any[] = [];

  selectedBequestTypes: string[] = [];

  minEstateValue?: number;
  maxEstateValue?: number;

  issueDateFrom?: Date;

  issueDateTo?: Date;
  pageSize = 50;

  cashAmount?: number;

  percentageAmount?: number;

  activeTab = 'candidates';

  paygEmails: PaygEmailAudit[] = [];

  paygPaid: PaygPaid[] = [];

  ngOnInit() {

    this.getPaygCandidates();

  }

  get selectedCount(): number {

    return this.filteredPaygCases.filter(x => x.sendEmail).length;

  }


  get withEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => x.email && x.email.trim() !== "")
      .length;
  }

  get withoutEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => !x.email || x.email.trim() === "")
      .length;
  }

  get withAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => x.aiEmail && x.aiEmail.trim() !== "")
      .length;
  }

  get withoutAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => !x.aiEmail || x.aiEmail.trim() === "")
      .length;
  }

  get foundAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x =>
        x.aiStatus?.toLowerCase() === "found"
      )
      .length;
  }

  openEmailDialog() {

  const selected = this.filteredPaygCases.filter(x => x.sendEmail);

  if (selected.length === 0) {

    alert("Please select at least one recipient.");

    return;

  }

  const dialogRef = this.dialog.open(AdminPaygEmailDialogComponent, {

    width: "1000px",

    maxWidth: "95vw",

    data: selected

  });

    dialogRef.afterClosed().subscribe(result => {

      if (!result) {
        return;
      }

      this.http.post(
        "/charitycentral/sendpaygemails",
        result
      ).subscribe({

        next: (result: any) => {

          alert(`${result.queued} email(s) queued successfully.`);

          this.getPaygCandidates();

          this.getPaygEmails();

        },

        error: (error) => {

          console.error(error);

          alert("Failed to queue emails.");

        }

      });

    });

}

  getPaygCandidates() {

    this.http.get<PaygCandidate[]>("/charitycentral/getpaygcandidates").subscribe(

      (result) => {

        this.paygCases = (result ?? []).map(x => ({
          ...x,
          bequestValue: this.calculateBequestValue(x),
          sendEmail: true
        }));

        this.bequestTypes = [...new Set(
          this.paygCases
            .map(x => x.bequestType)
            .filter(x => x != null && x !== "")
        )]
          .sort();

        this.applyFilter();

      },

      (error) => {

        console.error(error);

      }

    );

  }

  refresh() {

    if (this.selectedTabIndex === 0) {
      this.getPaygCandidates();
    }

    if (this.selectedTabIndex === 1) {
      this.getPaygEmails();
    }

    if (this.selectedTabIndex === 2) {
      this.getPaygPaid();
    }

  }

  applyFilter() {

    this.filteredPaygCases = this.filterCandidates(true);

    this.updatePagedData();

  }

  private filterCandidates(includeEmailFilter: boolean): PaygCandidate[] {

    const term = this.searchTerm.trim().toLowerCase();

    return this.paygCases.filter(x => {

      // -----------------------------
      // Email filter
      // -----------------------------

      if (includeEmailFilter) {

        const hasEmail =
          x.email != null &&
          x.email.trim() !== "";

        const hasAIEmail =
          x.aiEmail != null &&
          x.aiEmail.trim() !== "";

        const emailMatches =
          (hasEmail && this.showWithEmail) ||
          (!hasEmail && this.showWithoutEmail);

        const aiEmailMatches =
          (hasAIEmail && this.showWithAIEmail) ||
          (!hasAIEmail && this.showWithoutAIEmail);

        if (!emailMatches || !aiEmailMatches) {
          return false;
        }

        if (
          this.showFoundAIEmail &&
          x.aiStatus?.toLowerCase() !== "found"
        ) {
          return false;
        }

      }

      // -----------------------------
      // Bequest Type filter
      // -----------------------------

      const bequestMatches =
        this.selectedBequestTypes.length === 0 ||
        this.selectedBequestTypes.includes(x.bequestType ?? "");

      if (!bequestMatches) {
        return false;
      }

      // -----------------------------
      // Estate Value filter
      // -----------------------------

      const estate = x.estateValue ?? 0;

      if (
        this.minEstateValue != null &&
        estate < this.minEstateValue
      ) {
        return false;
      }

      if (
        this.maxEstateValue != null &&
        estate > this.maxEstateValue
      ) {
        return false;
      }

      // -----------------------------
      // Issue Date filter
      // -----------------------------

      if (x.issueDate) {

        const issueDate = new Date(x.issueDate);

        issueDate.setHours(0, 0, 0, 0);

        if (this.issueDateFrom) {

          const from = new Date(this.issueDateFrom);

          from.setHours(0, 0, 0, 0);

          if (issueDate < from) {
            return false;
          }

        }

        if (this.issueDateTo) {

          const to = new Date(this.issueDateTo);

          to.setHours(0, 0, 0, 0);

          if (issueDate > to) {
            return false;
          }

        }

      }
      else if (this.issueDateFrom || this.issueDateTo) {

        return false;

      }

      // -----------------------------
      // Search
      // -----------------------------

      if (term === "") {
        return true;
      }

      return (
        x.probateId?.toString().includes(term) ||
        x.beneficiaryName?.toLowerCase().includes(term) ||
        x.beneficiaryURN?.toLowerCase().includes(term) ||
        x.email?.toLowerCase().includes(term) ||
        x.aiEmail?.toLowerCase().includes(term)
      );

    });

  }

  selectAll(): void {
    this.filteredPaygCases.forEach(x => x.sendEmail = true);
  }

  deselectAll(): void {
    this.filteredPaygCases.forEach(x => x.sendEmail = false);
  }
  get allSelected(): boolean {
  return this.filteredPaygCases.length > 0 &&
         this.filteredPaygCases.every(x => x.sendEmail);
}


  selectedTabIndex = 0;

  tabChanged(index: number) {

    this.selectedTabIndex = index;

    if (index === 0) {
      this.getPaygCandidates();
    }

    if (index === 1) {
      this.getPaygEmails();
    }

    if (index === 2) {
      this.getPaygPaid();
    }

  }



  pageChanged(event: PageEvent) {

    this.pageSize = event.pageSize;

    const start = event.pageIndex * event.pageSize;

    const end = start + event.pageSize;

    this.pagedPaygCases = this.filteredPaygCases.slice(start, end);

  }

  private updatePagedData() {

    this.pagedPaygCases = this.filteredPaygCases.slice(0, this.pageSize);

    if (this.paginator) {

      this.paginator.firstPage();

    }

  }



  private calculateBequestValue(candidate: PaygCandidate): number | undefined {

    if (candidate.bequestType === "Absolute Pecuniary") {
      return candidate.estateValue;
    }

    if (candidate.bequestType === "Absolute Residual") {
      return candidate.percentageAmount;
    }

    return candidate.cashAmount;
  }


  getPaygEmails() {

    this.http
      .get<PaygEmailAudit[]>("/charitycentral/getpaygemails")
      .subscribe({

        next: (result) => {

          this.paygEmails = result ?? [];

        },

        error: (error) => {

          console.error("Failed to load PAYG emails", error);

        }

      });

  }


  getPaygPaid() {

    this.http
      .get<PaygPaid[]>("/charitycentral/getpaygpaid")
      .subscribe({

        next: (result) => {

          this.paygPaid = result ?? [];

        },

        error: (error) => {

          console.error("Failed to load PAYG paid payments", error);

        }

      });

  }

}
